declare global {
  interface Window {
    dataLayer: Array<Record<string, unknown>>;
  }
}

export enum GTM_EVENT {
  SEARCH_INITIATED = 'search_initiated',
  OUTBOUND_JOURNEY_SELECTED = 'outbound_journey_selected',
  INBOUND_JOURNEY_SELECTED = 'inbound_journey_selected',
  OUTBOUND_OFFER_CONFIRMED = 'outbound_offer_confirmed',
  INBOUND_OFFER_CONFIRMED = 'inbound_offer_confirmed',
  SEAT_SELECTION_OPENED = 'seat_selection_opened',
  SEAT_SELECTED = 'seat_selected',
  SEAT_SELECTION_COMPLETED = 'seat_selection_completed',
  BEGIN_CHECKOUT = 'begin_checkout',
  ADD_PAYMENT_INFO = 'add_payment_info',
}

export interface GTMPurchaseData extends Record<string, unknown> {
  event: 'purchase';
  transaction_id: string;
  value: number;
  currency: string;
}

const ensureDataLayer = () => {
  window.dataLayer = window.dataLayer || [];
};

const getGTMScriptSelector = (containerId: string) =>
  `script[src*="googletagmanager.com/gtm.js?id=${containerId}"]`;

const injectGTMScript = (containerId: string) => {
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${containerId}`;

  const firstScript = document.getElementsByTagName('script')[0];
  if (firstScript?.parentNode) {
    firstScript.parentNode.insertBefore(script, firstScript);
  } else {
    document.head.appendChild(script);
  }
};

const injectGTMNoscript = (containerId: string) => {
  const noscript = document.createElement('noscript');
  const iframe = document.createElement('iframe');
  iframe.src = `https://www.googletagmanager.com/ns.html?id=${containerId}`;
  iframe.height = '0';
  iframe.width = '0';
  iframe.style.display = 'none';
  iframe.style.visibility = 'hidden';
  noscript.appendChild(iframe);

  if (document.body.firstChild) {
    document.body.insertBefore(noscript, document.body.firstChild);
  } else {
    document.body.appendChild(noscript);
  }
};

export const initializeGTM = (containerId: string): boolean => {
  if (!containerId) {
    return false;
  }

  if (document.querySelector(getGTMScriptSelector(containerId))) {
    return true;
  }

  ensureDataLayer();

  window.dataLayer.push({
    'gtm.start': new Date().getTime(),
    event: 'gtm.js',
  });

  injectGTMScript(containerId);
  injectGTMNoscript(containerId);

  return true;
};

export const isGTMInitialized = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  if (!Array.isArray(window.dataLayer)) {
    return false;
  }

  return (
    document.querySelector('script[src*="googletagmanager.com/gtm.js"]') !==
    null
  );
};

export const trackPurchaseEvent = (purchaseData: GTMPurchaseData) => {
  if (!isGTMInitialized()) {
    return;
  }

  window.dataLayer.push(purchaseData);
};

export const trackCustomEvent = (
  eventName: string,
  eventData?: Record<string, unknown>
) => {
  if (!isGTMInitialized()) {
    return;
  }

  window.dataLayer.push({
    event: eventName,
    ...eventData,
  });
};
